import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en.json';
import fa from '../locales/fa.json';
import ar from '../locales/ar.json';
import tr from '../locales/tr.json';
import { DEFAULT_LOCALE, normalizeLocale, type AppLocale } from './config';
import { validatedRemoteTranslations } from './remoteTranslations';

const bundledResources = {
  en: { translation: en },
  fa: { translation: fa },
  ar: { translation: ar },
  tr: { translation: tr },
} as const;

let initialized = false;

/** Apply document `lang` + `dir` for layout and fonts. */
export function applyDocumentLocale(locale: AppLocale): void {
  const lang = normalizeLocale(locale);
  const rtl = lang === 'fa' || lang === 'ar';
  const dir = rtl ? 'rtl' : 'ltr';

  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest) {
    manifest.dataset.defaultHref ??= manifest.getAttribute('href') ?? '/manifest.webmanifest';
    manifest.setAttribute('href', lang === 'ar' ? '/manifest-ar.webmanifest' : manifest.dataset.defaultHref);
  }

  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
  document.documentElement.dataset.locale = lang;

  document.body.dir = dir;
  document.body.lang = lang;
  document.body.classList.toggle('app-rtl', rtl);
  document.body.classList.toggle('app-ltr', !rtl);

  const root = document.getElementById('root');
  if (root) {
    root.dir = dir;
    root.lang = lang;
  }
}

/**
 * Initialize i18next with bundled JSON fallback.
 * Remote API translations are merged later via `mergeRemoteTranslations`.
 */
export async function initI18n(locale: AppLocale = DEFAULT_LOCALE): Promise<typeof i18n> {
  const lng = normalizeLocale(locale);
  if (!initialized) {
    await i18n.use(initReactI18next).init({
      resources: { ...bundledResources },
      lng,
      fallbackLng: { ar: ['en'], default: [DEFAULT_LOCALE] },
      interpolation: { escapeValue: false },
      returnEmptyString: false,
    });
    initialized = true;
  } else if (i18n.language !== lng) {
    await i18n.changeLanguage(lng);
  }
  applyDocumentLocale(lng);
  return i18n;
}

/** Re-apply bundled auth.brandTagline after API/cache merge (ships with the app build). */
function reapplyBundledBrandTagline(lng: AppLocale): void {
  const bundled =
    lng === 'fa' ? fa : lng === 'ar' ? ar : lng === 'tr' ? tr : en;
  const tagline = bundled.auth?.brandTagline;
  if (!tagline) return;
  i18n.addResourceBundle(lng, 'translation', { auth: { brandTagline: tagline } }, true, true);
}

/** Deep-merge remote translation tree over bundled keys (API / admin edits). */
export function mergeRemoteTranslations(locale: AppLocale, remote: Record<string, unknown>): void {
  const lng = normalizeLocale(locale);
  const bundled = lng === 'ar' ? ar : lng === 'fa' ? fa : lng === 'tr' ? tr : en;
  i18n.addResourceBundle(lng, 'translation', validatedRemoteTranslations(lng, bundled, remote), true, true);
  reapplyBundledBrandTagline(lng);
}

export { i18n };
